// src/components/FormBuilder.tsx

import React, { useState, useCallback, useEffect } from "react";
import StepEditor from "./StepEditor";
import RuleEditor from "./RuleEditor";
import {
  FormConfig,
  QuestionnaireItem,
  ValidationRule,
  WizardStep,
  generateUniqueId,
  getAllQuestionIds,
} from "../../../../interfaces/dynamicForms/FormBuilder";
import FormInput from "./FormInput";
import JsonSyntaxHighlighter from "../../../../utilities/formattedDisplayComponents/jsonSyntaxHighlighter";
import { useAuth } from "../../../../firebase/AuthProvider";
import {
  getPdfTemplate,
  getSpecificConfig,
  saveFormConfiguration,
  uploadPdfTemplate,
} from "../../../../services/formsService/FormBuilderService";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import PdfMappingTab from "./PdfMappingTab";
import { PdfMappingDefinition } from "../../../../interfaces/dynamicForms/MappingTypes";
import backarrowbtn from "../../../../assets/images/backarrowbtn.svg";
import { Button } from "react-bootstrap";
import "./FormBuilder.css";

const initialFormConfig: FormConfig = {
  definition: {
    formId: "NewForm",
    steps: [],
  },
  rules: {
    formId: "NewForm",
    validationRules: [],
  },
  pdfMapping: {},
};

const FormBuilder: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [formConfig, setFormConfig] = useState<FormConfig>(initialFormConfig);
  const [pdfMapping, setPdfMapping] = useState<PdfMappingDefinition>({});
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState<
    "definition" | "rules" | "pdfMapping" | "json"
  >("definition");
  const [activeJsonTab, setActiveJsonTab] = useState<
    "definition" | "rules" | "mapping"
  >("definition");
  const { editFormId } = useParams<{ editFormId?: string }>();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState<boolean>(false);
  const [configError, setConfigError] = useState<string | null>(null);

  const handleBack = () => navigate("/form/fill");

  const reorderStep = useCallback(
    (stepId: string, direction: "up" | "down") => {
      setFormConfig((prev) => {
        const steps = [...prev.definition.steps];
        const idx = steps.findIndex((s) => s.id === stepId);
        if (idx === -1) return prev;
        const swapIdx = direction === "up" ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= steps.length) return prev;
        [steps[idx], steps[swapIdx]] = [steps[swapIdx], steps[idx]];
        return {
          ...prev,
          definition: { ...prev.definition, steps },
        };
      });
    },
    []
  );

  const reorderQuestion = useCallback(
    (stepId: string, questionId: string, direction: "up" | "down") => {
      setFormConfig((prev) => {
        const steps = prev.definition.steps.map((step) => {
          if (step.id !== stepId) return step;
          const qs = [...step.questions];
          const idx = qs.findIndex((q) => q.id === questionId);
          if (idx === -1) return step;
          const swapIdx = direction === "up" ? idx - 1 : idx + 1;
          if (swapIdx < 0 || swapIdx >= qs.length) return step;
          [qs[idx], qs[swapIdx]] = [qs[swapIdx], qs[idx]];
          return { ...step, questions: qs };
        });
        return {
          ...prev,
          definition: { ...prev.definition, steps },
        };
      });
    },
    []
  );

  const moveQuestionAcrossSteps = useCallback(
    (fromStepId: string, questionId: string, toStepId: string) => {
      setFormConfig((prev) => {
        // 1) find the question object
        const fromStep = prev.definition.steps.find((s) => s.id === fromStepId);
        if (!fromStep) return prev;
        const questionToMove = fromStep.questions.find(
          (q) => q.id === questionId
        );
        if (!questionToMove) return prev;

        // 2) build new steps array
        const steps = prev.definition.steps.map((step) => {
          if (step.id === fromStepId) {
            // remove from old step
            return {
              ...step,
              questions: step.questions.filter((q) => q.id !== questionId),
            };
          }
          if (step.id === toStepId) {
            // append to new step
            return {
              ...step,
              questions: [...step.questions, questionToMove],
            };
          }
          return step;
        });

        return {
          ...prev,
          definition: { ...prev.definition, steps },
        };
      });
    },
    []
  );

  useEffect(() => {
    // This function is now much simpler and more reliable
    const loadConfiguration = async (idToLoad: string) => {
      setIsLoadingConfig(true);
      setConfigError(null);
      setFormConfig(initialFormConfig);
      setPdfMapping({});
      setSubmitSuccess(false);

      if (!currentUser) return;
      const authToken = await currentUser.getIdToken();
      if (!authToken) {
        setConfigError("Authentication token not found.");
        setIsLoadingConfig(false);
        return;
      }

      try {
        // Fetch all required parts for THIS form specifically
        const [
          definitionResponse,
          rulesResponse,
          mappingResponse,
          pdfFileResponse,
        ] = await Promise.all([
          getSpecificConfig(idToLoad, "CONFIG", authToken),
          getSpecificConfig(idToLoad, "RULES", authToken),
          getSpecificConfig(idToLoad, "MAPPING", authToken),
          getPdfTemplate(idToLoad, authToken),
        ]);

        // The API returns the content of the JSON file, which is an object keyed by the formId.
        // We need to extract the inner object.
        const loadedDefinition = definitionResponse
          ? definitionResponse[idToLoad]
          : { formId: idToLoad, steps: [] };
        const loadedRules = rulesResponse
          ? rulesResponse[idToLoad]
          : { formId: idToLoad, validationRules: [] };
        const loadedMapping = mappingResponse ? mappingResponse[idToLoad] : {};

        console.log("LOADED MAPPING", loadedMapping);
        console.log("LOADED Config", loadedDefinition);
        console.log("LOADED rules", loadedRules);
        setFormConfig({ definition: loadedDefinition, rules: loadedRules });
        setPdfMapping(loadedMapping || {});
        setPdfFile(pdfFileResponse);
      } catch (err) {
        let message =
          "An unknown error occurred while loading the configuration.";
        if (err instanceof Error) message = err.message;
        setConfigError(message);
        setFormConfig(initialFormConfig);
      } finally {
        setIsLoadingConfig(false);
      }
    };

    if (editFormId) {
      loadConfiguration(editFormId);
    } else {
      setFormConfig(initialFormConfig);
      setPdfMapping({});
      setPdfFile(null);
      setIsLoadingConfig(false);
      setConfigError(null);
    }
  }, [editFormId, currentUser]);

  const handleNewPdfUpload = async (file: File) => {
    if (!editFormId || !currentUser) return;

    console.log("Handling new pdf upload");
    try {
      const authToken = await currentUser.getIdToken();
      await uploadPdfTemplate(editFormId, file, authToken);
      setPdfFile(file); // Update the local state to the new file
      alert("New PDF template uploaded successfully!");
    } catch (error) {
      console.error("Failed to upload new PDF template:", error);
      alert("Error: Could not upload the new PDF template.");
    }
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(true);

    if (!currentUser) {
      setSubmitError("You must be logged in to create a form.");
      setIsSubmitting(false);
      return;
    }
    const authToken = await currentUser.getIdToken();
    if (!authToken) {
      setSubmitError("Authentication token not found. Cannot save.");
      setIsSubmitting(false);
      return;
    }

    try {
      await saveFormConfiguration(formConfig, pdfMapping, authToken);
      setSubmitSuccess(true);
    } catch (err) {
      console.error("API Submission Error (FormBuilder):", err);
      let message = "An unknown error occurred during submission.";
      if (err instanceof Error) message = err.message;
      setSubmitError(`Failed to save configuration: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // NEW: The callback now updates the separate pdfMapping state
  const updatePdfMapping = useCallback((newMapping: PdfMappingDefinition) => {
    setPdfMapping(newMapping);
  }, []);

  const updateFormId = useCallback((newId: string) => {
    setFormConfig((prev) => ({
      ...prev,
      definition: { ...prev.definition, formId: newId },
      rules: { ...prev.rules, formId: newId },
    }));
  }, []);

  const addStep = useCallback(() => {
    setFormConfig((prevConfig) => {
      if (!prevConfig) return initialFormConfig;
      const newStep: WizardStep = {
        id: generateUniqueId("step"),
        label: "New Step",
        questions: [],
      };
      return {
        ...prevConfig,
        definition: {
          ...prevConfig.definition,
          steps: [...prevConfig.definition.steps, newStep],
        },
      };
    });
    setSubmitSuccess(false);
    setConfigError(null);
  }, []);

  const deleteStep = useCallback((stepId: string) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      definition: {
        ...prevConfig.definition,
        steps: prevConfig.definition.steps.filter((step) => step.id !== stepId),
      },
      // Note: This does not automatically clean up validation rules that may reference questions in the deleted step.
    }));
  }, []);

  const updateStep = useCallback(
    (stepId: string, updatedStepData: Partial<WizardStep>) => {
      setFormConfig((prevConfig) => ({
        ...prevConfig,
        definition: {
          ...prevConfig.definition,
          steps: prevConfig.definition.steps.map((step) =>
            step.id === stepId ? { ...step, ...updatedStepData } : step
          ),
        },
      }));
    },
    []
  );

  const addQuestion = useCallback(
    (stepId: string, newQuestion: QuestionnaireItem) => {
      setFormConfig((prevConfig) => ({
        ...prevConfig,
        definition: {
          ...prevConfig.definition,
          steps: prevConfig.definition.steps.map((step) =>
            step.id === stepId
              ? { ...step, questions: [...step.questions, newQuestion] }
              : step
          ),
        },
      }));
    },
    []
  );

  const deleteQuestion = useCallback((stepId: string, questionId: string) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      definition: {
        ...prevConfig.definition,
        steps: prevConfig.definition.steps.map((step) =>
          step.id === stepId
            ? {
                ...step,
                questions: step.questions.filter((q) => q.id !== questionId),
              }
            : step
        ),
      },
      // Note: This does not automatically clean up validation rules that may reference the deleted question.
    }));
  }, []);

  const updateQuestion = useCallback(
    (
      stepId: string,
      questionId: string,
      updatedQuestion: QuestionnaireItem
    ) => {
      setFormConfig((prevConfig) => ({
        ...prevConfig,
        definition: {
          ...prevConfig.definition,
          steps: prevConfig.definition.steps.map((step) =>
            step.id === stepId
              ? {
                  ...step,
                  questions: step.questions.map((q) =>
                    q.id === questionId ? updatedQuestion : q
                  ),
                }
              : step
          ),
        },
      }));
    },
    []
  );

  const addRule = useCallback(() => {
    const newRule: ValidationRule = {
      id: generateUniqueId("rule"),
      description: "New Validation Rule",
      checks: [],
      errorMessage: "Validation failed.",
      errorField: "",
    };
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      rules: {
        ...prevConfig.rules,
        validationRules: [...prevConfig.rules.validationRules, newRule],
      },
    }));
  }, []);

  const deleteRule = useCallback((ruleId: string) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      rules: {
        ...prevConfig.rules,
        validationRules: prevConfig.rules.validationRules.filter(
          (rule) => rule.id !== ruleId
        ),
      },
    }));
  }, []);

  const updateRule = useCallback(
    (ruleId: string, updatedRuleData: Partial<ValidationRule>) => {
      setFormConfig((prevConfig) => ({
        ...prevConfig,
        rules: {
          ...prevConfig.rules,
          validationRules: prevConfig.rules.validationRules.map((rule) =>
            rule.id === ruleId ? { ...rule, ...updatedRuleData } : rule
          ),
        },
      }));
    },
    []
  );

  // RESTORED: Full JSX for error and loading states
  if (isLoadingConfig) {
    return (
      <div className="container mx-auto p-8 text-center">
        <p className="text-lg text-gray-600">Loading Configuration...</p>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="container mx-auto p-8">
        <div className="p-4 rounded bg-red-100 text-red-800 border border-red-300">
          <h2 className="text-xl font-bold mb-2">
            Error Loading Configuration
          </h2>
          <p>{configError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  if (!formConfig) {
    return (
      <div className="container mx-auto p-8 text-center text-gray-500">
        Form configuration is not available.
      </div>
    );
  }

  return (
    <>
      <div className="common_card">
        <div className="top border_tittle">
            <div className="top_bar_tittle">
                <h2>
                  <button onClick={handleBack}><img src={backarrowbtn} alt="back arrow" className="icon_sm" /></button>
                  Sprout Forms:{" "} {editFormId ? `Editing Form: ${formConfig.definition.formId}` : "New Form"}
                </h2>
            </div>
        </div>
        
        <div className="configuration_flx labelBlock">
          <FormInput
            label="Form ID"
            id="formId"
            value={formConfig.definition.formId}
            onChange={(e) => updateFormId(e.target.value)}
            className="block"
          />
          <Button variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`btn_sm ${
              isSubmitting
                ? ""
                : ""
            }`}
          >
            {isSubmitting ? "Saving..." : "Save Configuration"}
          </Button>
        </div>

        {submitSuccess && (
          <div className="mb-4 p-3 rounded bg-green-100 text-green-800 border border-green-300 text-sm">
            Form configuration saved successfully!
          </div>
        )}
        {submitError && (
          <div className="mb-4 p-3 rounded bg-red-100 text-red-800 border border-red-300 text-sm">
            <strong>Error:</strong> {submitError}
          </div>
        )}

        <div className="d-flex my-3">
          <nav className="save_configuration_tab" aria-label="Tabs">
            <button
              onClick={() => setActiveTab("definition")}
              className={`${
                activeTab === "definition"
                  ? "active"
                  : ""
              } `}
            >
              Form Definition
            </button>
            <button
              onClick={() => setActiveTab("rules")}
              className={`${
                activeTab === "rules"
                  ? "active"
                  : ""
              } `}
            >
              Validation Rules
            </button>
            <button
              onClick={() => setActiveTab("pdfMapping")}
              className={`${
                activeTab === "pdfMapping"
                  ? "active"
                  : ""
              } `}
            >
              PDF Mapping
            </button>
            <button
              onClick={() => setActiveTab("json")}
              className={`${
                activeTab === "json"
                  ? "active"
                  : ""
              } `}
            >
              Generated JSON
            </button>
          </nav>
        </div>

        <div>
          {activeTab === "definition" && (
            <div>
              <div className="top">
                <div className="top_bar_tittle">
                  <h2>Steps</h2>
                </div>
              </div>
              {formConfig.definition.steps.map((step, i) => (
                <StepEditor
                  key={step.id}
                  stepOrder={i}
                  step={step}
                  isFirstStep={i === 0}
                  isLastStep={i === formConfig.definition.steps.length - 1}
                  reorderStep={reorderStep}
                  reorderQuestion={reorderQuestion}
                  formDefinition={formConfig.definition}
                  updateStep={updateStep}
                  deleteStep={deleteStep}
                  addQuestion={addQuestion}
                  updateQuestion={updateQuestion}
                  deleteQuestion={deleteQuestion}
                  moveQuestionAcrossSteps={moveQuestionAcrossSteps}
                  totalSteps={formConfig.definition.steps.length}
                />
              ))}
              <Button variant="success" onClick={addStep}className="btn_sm">Add Step</Button>
            </div>
          )}

          {activeTab === "rules" && (
            <div>
              <div className="top">
                <div className="top_bar_tittle">
                  <h2>Validation Rules</h2>
                </div>
              </div>
              {formConfig.rules.validationRules.map((rule) => (
                <RuleEditor
                  key={rule.id}
                  rule={rule}
                  formDefinition={formConfig.definition}
                  updateRule={updateRule}
                  deleteRule={deleteRule}
                />
              ))}
              <button
                onClick={addRule}
                className="bg-purple-600 hover:bg-purple-800 text-white px-5 py-2 rounded font-semibold mt-4"
              >
                + Add Rule
              </button>
            </div>
          )}

          {activeTab === "pdfMapping" && (
            <PdfMappingTab
              formDefinition={formConfig.definition}
              pdfMapping={pdfMapping}
              onMappingChange={updatePdfMapping}
              allQuestionIds={getAllQuestionIds(formConfig.definition)}
              initialPdfFile={pdfFile}
              onNewPdfUpload={handleNewPdfUpload}
            />
          )}

          {activeTab === "json" && (
            <div>
              <h2 className="text-2xl font-semibold mb-2 text-gray-700">
                Generated JSON Configuration
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                View the raw JSON for each part of your form's configuration.
              </p>

              {/* Nested Tab Navigation */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-6" aria-label="JSON Views">
                  <button
                    onClick={() => setActiveJsonTab("definition")}
                    className={`${
                      activeJsonTab === "definition"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
                  >
                    Form Definition
                  </button>
                  <button
                    onClick={() => setActiveJsonTab("rules")}
                    className={`${
                      activeJsonTab === "rules"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
                  >
                    Validation Rules
                  </button>
                  <button
                    onClick={() => setActiveJsonTab("mapping")}
                    className={`${
                      activeJsonTab === "mapping"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
                  >
                    PDF Mapping
                  </button>
                </nav>
              </div>

              {/* Nested Tab Content */}
              <div className="mt-4">
                {activeJsonTab === "definition" && (
                  <div>
                    <h3 className="text-lg font-medium mb-2">
                      Form Definition
                    </h3>
                    <pre className="bg-gray-800 text-white p-4 rounded-md overflow-x-auto text-sm">
                      <JsonSyntaxHighlighter
                        jsonString={JSON.stringify(
                          {
                            [formConfig.definition.formId]:
                              formConfig.definition,
                          },
                          null,
                          2
                        )}
                      />
                    </pre>
                  </div>
                )}
                {activeJsonTab === "rules" && (
                  <div>
                    <h3 className="text-lg font-medium mb-2">
                      Validation Rules
                    </h3>
                    <pre className="bg-gray-800 text-white p-4 rounded-md overflow-x-auto text-sm">
                      <JsonSyntaxHighlighter
                        jsonString={JSON.stringify(
                          { [formConfig.rules.formId]: formConfig.rules },
                          null,
                          2
                        )}
                      />
                    </pre>
                  </div>
                )}
                {activeJsonTab === "mapping" && (
                  <div>
                    <h3 className="text-lg font-medium mb-2">PDF Mapping</h3>
                    <pre className="bg-gray-800 text-white p-4 rounded-md overflow-x-auto text-sm">
                      <JsonSyntaxHighlighter
                        jsonString={JSON.stringify(
                          {
                            [`${formConfig.definition.formId}.json`]:
                              pdfMapping,
                          },
                          null,
                          2
                        )}
                      />
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FormBuilder;
